import { BlockerFunction } from "@remix-run/router";
import { useCallback, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";
import { FieldValues } from "react-hook-form/dist/types/fields";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

export const useFormNavigationBlocker = <T extends FieldValues>(
  form: UseFormReturn<T>,
  isDisabled?: boolean,
) => {
  const { t } = useTranslation(["common"]);

  const { isDirty } = form.formState;

  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) => {
        if (isDisabled) {
          return false;
        }
        // Don't block if only query params changed
        if (currentLocation.pathname === nextLocation.pathname) {
          return false;
        }
        return isDirty;
      },
      [isDirty, isDisabled],
    ),
  );

  useEffect(() => {
    if (blocker.state === "blocked" && !isDirty) {
      blocker.reset();
    }
  }, [blocker, isDirty]);

  useEffect(() => {
    if (isDisabled) {
      return;
    }
    const listener = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = t("common:unsavedProgress");
      }
    };
    window.addEventListener("beforeunload", listener, { capture: true });
    return () => {
      window.removeEventListener("beforeunload", listener, { capture: true });
    };
  }, [t, isDirty, isDisabled]);

  return blocker;
};
